import { useEffect, useState } from "react";
import { api, fmtINR, fmtDateTime } from "@/lib/api";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, Landmark, ReceiptText, ArrowRight, PlusCircle, HandCoins, BarChart3 } from "lucide-react";

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setD(r.data)).catch((e) => setErr(e?.message || "err"));
  }, []);

  if (err) return <div className="text-red-600">{err}</div>;
  if (!d) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Real-time view of cash & bank collections across all 7 branches.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="stat-card-primary" data-testid="stat-cash-in-hand">
          <div className="flex items-center justify-between">
            <div className="text-blue-100 text-sm font-medium">Cash in Hand</div>
            <Wallet className="h-5 w-5 text-blue-200" />
          </div>
          <div className="text-3xl font-extrabold mt-4 tracking-tight">{fmtINR(d.cash_in_hand)}</div>
          <div className="text-blue-200 text-xs mt-2">Available for deposit / handover</div>
        </div>

        <StatCard label="Today's Cash Collection" value={fmtINR(d.today_cash)} icon={TrendingUp} accent="green" testid="stat-today-cash" />
        <StatCard label="Today's Bank Collection" value={fmtINR(d.today_bank)} icon={Landmark} accent="indigo" testid="stat-today-bank" />
        <StatCard label="Today's Receipts" value={d.today_receipts_count} icon={ReceiptText} accent="amber" testid="stat-today-count" />
      </div>

      {/* Quick actions */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-800">Recent Transactions</h2>
            <Link to="/receipts" className="text-xs font-semibold text-blue-600 flex items-center gap-1">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {d.recent.length === 0 && <div className="py-6 text-center text-slate-400 text-sm">No transactions yet</div>}
            {d.recent.map((r, i) => (
              <div key={`${r.time}-${r.type}-${i}`} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium text-slate-800">{r.description}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{fmtDateTime(r.time)} · {r.type}</div>
                </div>
                <div className={`mono font-bold ${r.amount < 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {r.amount < 0 ? "−" : "+"} {fmtINR(Math.abs(r.amount))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="font-bold text-slate-800 mb-2">Quick Actions</h2>
          <QuickAction to="/new-receipt" label="New Money Receipt" icon={PlusCircle} color="blue" testid="qa-new-receipt" />
          <QuickAction to="/deposit" label="Deposit to Bank" icon={Landmark} color="emerald" testid="qa-deposit" />
          <QuickAction to="/handover" label="Handover to Owner" icon={HandCoins} color="amber" testid="qa-handover" />
          <QuickAction to="/reports" label="Reports" icon={BarChart3} color="slate" testid="qa-reports" />
        </div>
      </div>

      {/* Branch breakdown */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-800 mb-4">Collection by Branch (All-time)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2">Branch</th>
                <th className="py-2 text-right">Receipts</th>
                <th className="py-2 text-right">Cash</th>
                <th className="py-2 text-right">Bank (UPI/NEFT/RTGS/Cheque)</th>
                <th className="py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {d.by_branch.map((b) => (
                <tr key={b.code} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 font-semibold text-slate-800">{b.name} <span className="text-xs text-slate-400 ml-1">({b.code})</span></td>
                  <td className="py-3 text-right mono">{b.count}</td>
                  <td className="py-3 text-right mono text-emerald-700 font-semibold">{fmtINR(b.cash)}</td>
                  <td className="py-3 text-right mono text-blue-700 font-semibold">{fmtINR(b.bank)}</td>
                  <td className="py-3 text-right mono font-bold">{fmtINR(b.cash + b.bank)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment-mode breakdown */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-800 mb-4">Total by Payment Mode</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(d.mode_totals).map(([m, v]) => (
            <div key={m} className="rounded-xl border border-slate-200 p-4">
              <div className="text-xs uppercase text-slate-500 tracking-wider">{m}</div>
              <div className="text-lg font-bold mono mt-1 text-slate-800">{fmtINR(v)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  const map = {
    green: "text-emerald-600 bg-emerald-50",
    indigo: "text-indigo-600 bg-indigo-50",
    amber: "text-amber-600 bg-amber-50",
  };
  return (
    <div className="stat-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-slate-500 text-sm font-medium">{label}</div>
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${map[accent]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="text-2xl font-extrabold mt-4 text-slate-900 tracking-tight">{value}</div>
    </div>
  );
}

function QuickAction({ to, label, icon: Icon, color, testid }) {
  const map = {
    blue: "from-blue-600 to-blue-700 text-white",
    emerald: "from-emerald-600 to-emerald-700 text-white",
    amber: "from-amber-500 to-amber-600 text-white",
    slate: "from-slate-700 to-slate-800 text-white",
  };
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`block bg-gradient-to-r ${map[color]} rounded-xl p-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Icon className="h-4 w-4" /> {label}
        </div>
        <ArrowRight className="h-4 w-4 opacity-80" />
      </div>
    </Link>
  );
}
